import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Link } from "react-router-dom"
import { Helmet } from "react-helmet-async"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  DollarSign,
  Clock,
  TrendingUp,
  Search,
  Zap,
  Map,
  Lock,
  Shield,
  Package,
  Wrench,
  Link as LinkIcon,
  BarChart3,
  CheckCircle,
  FileText,
  RefreshCw,
  Users
} from "lucide-react"
import { FuseguLogo } from "@/components/FuseguLogo"
import { useAuth } from "@/contexts/AuthContext"

export default function DemoPage() {
  const { user } = useAuth()
  // Demo data to showcase what's possible
  const demoScans = [
    {
      id: "demo-001",
      target: "https://example-ecommerce.com",
      type: "Comprehensive",
      status: "completed",
      vulnerabilities: 12,
      securityScore: 73,
      date: "2024-01-15",
    },
    {
      id: "demo-002",
      target: "https://financial-app.com",
      type: "OWASP ZAP",
      status: "completed",
      vulnerabilities: 3,
      securityScore: 89,
      date: "2024-01-14",
    },
    {
      id: "demo-003",
      target: "https://healthcare-portal.net",
      type: "Nuclei",
      status: "completed",
      vulnerabilities: 7,
      securityScore: 82,
      date: "2024-01-13",
    },
  ]

  const securityTools = [
    { name: "OWASP ZAP", description: "Web application security scanner", Icon: Search },
    { name: "Nuclei", description: "Template-based vulnerability detection", Icon: Zap },
    { name: "Nmap", description: "Network discovery and security auditing", Icon: Map },
    { name: "SSL Scanner", description: "Certificate and TLS configuration analysis", Icon: Lock },
    { name: "Security Headers", description: "HTTP security headers validation", Icon: Shield },
    { name: "NPM Audit", description: "Node.js dependency vulnerability scan", Icon: Package },
    { name: "JavaScript Analysis", description: "Client-side code security review", Icon: Wrench },
    { name: "API Discovery", description: "Endpoint discovery and testing", Icon: LinkIcon },
  ]

  const getStatusBadge = () => {
    return <Badge variant="default">Completed</Badge>
  }

  const getVulnBadge = (count: number) => {
    if (count === 0) return <Badge variant="default">Clean</Badge>
    if (count <= 5) return <Badge variant="secondary">{count} Issues</Badge>
    return <Badge variant="destructive">{count} Issues</Badge>
  }

  const getScoreBadge = (score: number) => {
    if (score >= 90) return <Badge className="bg-green-100 text-green-800">{score}/100</Badge>
    if (score >= 70) return <Badge className="bg-yellow-100 text-yellow-800">{score}/100</Badge>
    return <Badge className="bg-red-100 text-red-800">{score}/100</Badge>
  }

  return (
    <>
      <Helmet>
        <title>Fusegu - Multi-Tool Security Scanning | Find Vulnerabilities</title>
        <meta 
          name="description" 
          content="Run 10+ security tools simultaneously. Comprehensive vulnerability scanning with Nmap, Nikto, SQLMap, OWASP ZAP and more. Start free trial today." 
        />
        <meta property="og:title" content="Fusegu - Multi-Tool Security Scanning Platform" />
        <meta property="og:description" content="Enterprise-grade security testing. Run 10+ security tools simultaneously. Find vulnerabilities before attackers do." />
        <meta property="og:url" content="https://fusegusecurity.com/" />
        <meta name="twitter:title" content="Fusegu - Multi-Tool Security Scanning Platform" />
        <meta name="twitter:description" content="Enterprise-grade security testing. Run 10+ security tools simultaneously. Find vulnerabilities before attackers do." />
      </Helmet>
      <div className="flex min-h-screen w-full flex-col">
        {/* Header */}
        <header className="sticky top-0 flex h-16 items-center gap-4 border-b bg-background px-4 md:px-6 z-50">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-2">
            <FuseguLogo className="h-6 w-6 flex-shrink-0" />
            <span className="text-lg font-bold">Fusegu</span>
          </div>
          <div className="flex items-center gap-4">
            {user ? (
              <Link to="/dashboard">
                <Button>Go to Dashboard</Button>
              </Link>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost">Sign In</Button>
                </Link>
                <Link to="/register">
                  <Button>Start Free Trial</Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1">
        {/* Hero Section */}
        <section className="py-20 px-4 md:px-8 bg-gradient-to-br from-slate-50 to-blue-50">
          <div className="max-w-6xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              <span className="text-gray-900">Comprehensive</span>
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"> Security Testing</span>
              <br /><span className="text-gray-900">for Modern Applications</span>
            </h1>
            <p className="text-xl text-gray-700 mb-4 max-w-3xl mx-auto font-medium">
              Run 10+ security tools simultaneously. Find vulnerabilities before attackers do.
              Protect your business with enterprise-grade security assessments.
            </p>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-900 rounded-full font-medium mb-6">
              <Clock className="h-4 w-4" />
              <span>Start your 7-day trial today — 3 scans per day on any sites, full feature access</span>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button size="lg" className="text-lg px-8 py-3">
                  Start Free Security Scan
                </Button>
              </Link>
            </div>
            <p className="text-sm text-gray-500 mt-4">
              No credit card required • 7-day free trial • 3 scans per day on any sites
            </p>
          </div>
        </section>

        {/* Why You Need Security Testing */}
        <section className="py-16 px-4 md:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Why Your Website Needs Security Testing
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Cyber attacks happen every 39 seconds. Don't be the next victim.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <Card className="text-center">
                <CardHeader>
                  <div className="mx-auto w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                    <DollarSign className="h-6 w-6 text-red-600" />
                  </div>
                  <CardTitle>Average Breach Cost: $4.88M</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    The average cost of a data breach in 2024. Proactive security testing can prevent most incidents.
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center">
                <CardHeader>
                  <div className="mx-auto w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                    <Clock className="h-6 w-6 text-orange-600" />
                  </div>
                  <CardTitle>204 Days to Detect</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Average time to identify a breach. Automated scanning finds vulnerabilities instantly.
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center">
                <CardHeader>
                  <div className="mx-auto w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                    <TrendingUp className="h-6 w-6 text-blue-600" />
                  </div>
                  <CardTitle>Growing Threat Landscape</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Web application attacks continue rising. Stay protected with continuous security monitoring.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Cost Comparison Section */}
        <section className="py-16 px-4 md:px-8 bg-gradient-to-br from-slate-50 to-gray-100">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Why <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Fusegu</span> is the Smart Choice
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Get enterprise-grade security testing at a fraction of the cost. See how we compare to traditional pentesting and compliance platforms.
              </p>
            </div>

            <Card className="border-2 shadow-lg">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="font-bold text-base">Solution</TableHead>
                      <TableHead className="font-bold text-base">Annual Cost</TableHead>
                      <TableHead className="font-bold text-base">Time to Results</TableHead>
                      <TableHead className="font-bold text-base">Coverage</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {/* Fusegu */}
                    <TableRow className="bg-green-50 border-2 border-green-200">
                      <TableCell className="font-semibold">
                        <div className="flex items-center gap-2">
                          <p className="text-base">Fusegu Security</p>
                          <Badge className="bg-green-600">Best Value</Badge>
                        </div>
                        <p className="text-xs text-gray-500">Automated security platform</p>
                      </TableCell>
                      <TableCell>
                        <div className="text-green-600 font-bold">$49/month</div>
                        <p className="text-xs text-gray-500">per account ($588/year)</p>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium text-green-600">8 minutes</div>
                        <p className="text-xs text-gray-500">Instant, on-demand</p>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <Badge className="bg-green-600">Comprehensive</Badge>
                          <p className="text-xs text-gray-500">10+ security tools</p>
                        </div>
                      </TableCell>
                    </TableRow>

                    {/* Manual Pentest */}
                    <TableRow>
                      <TableCell className="font-semibold">
                        <div>
                          <p className="text-base">Manual Pentest</p>
                          <p className="text-xs text-gray-500">Traditional security audit</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-red-600 font-bold">$5,000 - $15,000</div>
                        <p className="text-xs text-gray-500">Per test</p>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">2-3 weeks</div>
                        <p className="text-xs text-gray-500">Plus scheduling delays</p>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <Badge variant="outline" className="bg-yellow-50 text-yellow-700">Limited scope</Badge>
                          <p className="text-xs text-gray-500">Manual, point-in-time</p>
                        </div>
                      </TableCell>
                    </TableRow>

                    {/* Vanta/Drata */}
                    <TableRow>
                      <TableCell className="font-semibold">
                        <div>
                          <p className="text-base">Vanta / Drata</p>
                          <p className="text-xs text-gray-500">Compliance platforms</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-orange-600 font-bold">$10,000 - $30,000+</div>
                        <p className="text-xs text-gray-500">Annual subscription</p>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">2-3 weeks</div>
                        <p className="text-xs text-gray-500">Plus integration time</p>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <Badge variant="outline" className="bg-blue-50 text-blue-700">Compliance focus</Badge>
                          <p className="text-xs text-gray-500">Organizational controls</p>
                        </div>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Key Advantages */}
            <div className="grid md:grid-cols-3 gap-6 mt-12">
              <Card className="text-center border-2 border-green-100">
                <CardHeader>
                  <div className="mx-auto w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                    <DollarSign className="h-6 w-6 text-green-600" />
                  </div>
                  <CardTitle className="text-green-700">98% Cost Savings</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Save thousands compared to manual pentests and compliance platforms. Get enterprise-grade security at startup prices.
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center border-2 border-blue-100">
                <CardHeader>
                  <div className="mx-auto w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                    <Clock className="h-6 w-6 text-blue-600" />
                  </div>
                  <CardTitle className="text-blue-700">250x Faster</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Get results in minutes, not weeks. Run scans as often as you need with no scheduling delays.
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center border-2 border-purple-100">
                <CardHeader>
                  <div className="mx-auto w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                    <Shield className="h-6 w-6 text-purple-600" />
                  </div>
                  <CardTitle className="text-purple-700">More Comprehensive</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Run 10+ industry-standard tools simultaneously. Find vulnerabilities compliance platforms miss.
                  </p>
                </CardContent>
              </Card>
            </div>

            <div className="text-center mt-8">
              <Link to="/register">
                <Button size="lg" className="text-lg px-8">
                  Start Free Security Scan
                </Button>
              </Link>
              <p className="text-sm text-gray-500 mt-3">
                No credit card required • 7-day free trial • 3 scans per day on any sites
              </p>
            </div>
          </div>
        </section>

        {/* SOC2 Compliance Feature */}
        <section className="py-16 px-4 md:px-8 bg-blue-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-full font-medium mb-4">
                <span>SOC2 Technical Compliance</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Automated SOC2 Compliance Scanning
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Run comprehensive scans that automatically map findings to SOC2 technical controls.
                Generate professional compliance reports to share with technical teams and auditors.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-8">
              {/* Mock SOC2 Demo Card */}
              <Card className="border-2 border-blue-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-blue-600" />
                    SOC2 Technical Controls
                  </CardTitle>
                  <CardDescription>
                    Automated mapping to 4 scannable technical controls
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                    <div>
                      <p className="font-semibold text-sm">CC6.1 - Logical Access</p>
                      <p className="text-xs text-gray-600">Access control validation</p>
                    </div>
                    <Badge className="bg-green-100 text-green-800">92%</Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                    <div>
                      <p className="font-semibold text-sm">CC6.6 - Network Security</p>
                      <p className="text-xs text-gray-600">Network threat detection</p>
                    </div>
                    <Badge className="bg-yellow-100 text-yellow-800">78%</Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg border border-orange-200">
                    <div>
                      <p className="font-semibold text-sm">CC6.7 - Transmission Security</p>
                      <p className="text-xs text-gray-600">Data transmission protection</p>
                    </div>
                    <Badge className="bg-orange-100 text-orange-800">65%</Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                    <div>
                      <p className="font-semibold text-sm">CC6.8 - Malicious Code</p>
                      <p className="text-xs text-gray-600">Protection & detection</p>
                    </div>
                    <Badge className="bg-green-100 text-green-800">88%</Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Benefits List */}
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <CheckCircle className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Automated Control Mapping</h3>
                    <p className="text-gray-600">Every vulnerability is automatically mapped to relevant SOC2 controls</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <FileText className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Compliance Reports</h3>
                    <p className="text-gray-600">Generate detailed reports for technical teams and compliance auditors</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <RefreshCw className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Comprehensive Scanning</h3>
                    <p className="text-gray-600">Run all security tools simultaneously with SOC2 compliance built-in</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Users className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Share with Teams</h3>
                    <p className="text-gray-600">Easy-to-understand reports that technical teams can act on immediately</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center mt-8">
              <Link to="/register">
                <Button size="lg" className="bg-blue-600 hover:bg-blue-700">
                  Start Free Security Scan
                </Button>
              </Link>
              <p className="text-sm text-gray-500 mt-3">
                7-day free trial with 3 scans per day on any sites • Full access to all features
              </p>
            </div>
          </div>
        </section>

        {/* Security Tools Preview */}
        <section className="py-16 px-4 md:px-8 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                10+ Professional Security Tools
              </h2>
              <p className="text-lg text-gray-600">
                Enterprise-grade security testing in one comprehensive platform
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {securityTools.map((tool, index) => (
                <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <div className="mx-auto w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-3">
                      <tool.Icon className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="font-semibold text-lg mb-2">{tool.name}</h3>
                    <p className="text-sm text-gray-600">{tool.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="text-center">
              <Link to="/register">
                <Button size="lg">
                  Start Free Security Scan
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Sample Results Preview */}
        <section className="py-16 px-4 md:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                See What You'll Get
              </h2>
              <p className="text-lg text-gray-600">
                Sample security scan results from real assessments
              </p>
            </div>

            {/* Service Features */}
            <div className="grid gap-4 md:grid-cols-2 md:gap-8 lg:grid-cols-4 mb-8">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Security Tools</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">10+</div>
                  <p className="text-xs text-muted-foreground">
                    Industry-standard scanners
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">White-Label Reports</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">✓</div>
                  <p className="text-xs text-muted-foreground">
                    Professional branded PDFs
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Scan Time</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">~10 min</div>
                  <p className="text-xs text-muted-foreground">
                    Comprehensive assessment
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Vulnerability Types</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">OWASP</div>
                  <p className="text-xs text-muted-foreground">
                    Top 10 + custom patterns
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Sample Results Table */}
            <Card>
              <CardHeader>
                <CardTitle>Sample Security Assessment Results</CardTitle>
                <CardDescription>
                  Real examples of vulnerabilities found in recent scans (anonymized)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Website Type</TableHead>
                      <TableHead>Scan Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Issues Found</TableHead>
                      <TableHead>Security Score</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {demoScans.map((scan) => (
                      <TableRow key={scan.id}>
                        <TableCell className="font-medium">
                          {scan.target}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{scan.type}</Badge>
                        </TableCell>
                        <TableCell>
                          {getStatusBadge()}
                        </TableCell>
                        <TableCell>
                          {getVulnBadge(scan.vulnerabilities)}
                        </TableCell>
                        <TableCell>
                          {getScoreBadge(scan.securityScore)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 md:px-8 security-gradient text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-5xl font-bold mb-6">
              Start Securing Your Applications Today
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Discover vulnerabilities before attackers do with our comprehensive 
              security assessment platform.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button size="lg" variant="secondary" className="text-lg px-8 py-3">
                  Start Free Security Scan
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" className="text-lg px-8 py-3 border-white text-white bg-transparent hover:bg-white hover:text-blue-600">
                  Sign In
                </Button>
              </Link>
            </div>
            <div className="mt-8 flex items-center justify-center gap-8 text-sm opacity-75">
              <div className="flex items-center gap-2">
                <span>✓</span> 7-day free trial
              </div>
              <div className="flex items-center gap-2">
                <span>✓</span> 3 scans per day on any sites
              </div>
              <div className="flex items-center gap-2">
                <span>✓</span> No credit card required
              </div>
            </div>
            <p className="mt-4 text-sm opacity-75">
              Upgrade to Pro for 50 scans/day on any sites at $49/month
            </p>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t py-8 px-4 md:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-500">
              © 2025 Fusegu Security. Professional vulnerability assessment platform.
            </p>
            <div className="flex gap-6 text-sm">
              <Link to="/blogs" className="text-gray-600 hover:text-gray-900">
                Resources
              </Link>
              <Link to="/privacy" className="text-gray-600 hover:text-gray-900">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-gray-600 hover:text-gray-900">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </footer>
      </div>
    </>
  )
}